import { inject, Injectable } from '@angular/core';

import moment from 'moment';

import { DeliveryCalendar } from '@cakeiteasy/web-ui';
import { PickupCalendarDay, ToDoorCalendarDay } from '@cakeiteasy/web-utils';

import { ReturnPackagingApiService } from './return-packaging-api.service';

const MONTHS_TO_LOAD = 2;

export interface IReturnTimeSlot {
  timeRange: string;
  /**Null when the source calendar quotes no price, as the outlet calendar does for self-returns.*/
  price: number | null;
}

interface IReturnCalendarRequest {
  monthDate: string;
  prevCalendar?: DeliveryCalendar;
}

/**Owns every shape difference between the outlet and delivery calendars, so the schedule
 * components can treat both as a DeliveryCalendar of IReturnTimeSlot days.*/
@Injectable({ providedIn: 'root' })
export class ReturnCalendarService {
  private readonly _api = inject(ReturnPackagingApiService);

  public loadSelfReturnCalendar(request: IReturnCalendarRequest & { outletId: number }): Promise<DeliveryCalendar> {
    const { outletId } = request;

    return this._load((year, month) => this._api.getOutletCalendar(outletId, year, month), request);
  }

  public loadCourierReturnCalendar(
    request: IReturnCalendarRequest & { postNumber: string },
  ): Promise<DeliveryCalendar> {
    const { postNumber } = request;

    return this._load((year, month) => this._api.getDeliveryCalendar(postNumber, year, month), request);
  }

  /**The outlet calendar quotes plain time strings and no per-slot price.*/
  public getSelfReturnSlots(day: PickupCalendarDay): IReturnTimeSlot[] {
    const schedule = (day && day.schedule) || [];

    return schedule.map((timeRange) => ({ timeRange, price: null }));
  }

  /**The delivery calendar nests its slots under the last variant, the way delivery-to-door reads them.*/
  public getCourierReturnSlots(day: ToDoorCalendarDay): IReturnTimeSlot[] {
    const variants = (day && day.variants) || [];
    const variant = variants[variants.length - 1];
    const schedule = (variant && variant.schedule) || [];

    return schedule.map(({ timeRange, price }) => ({ timeRange, price: price || 0 }));
  }

  private async _load<T>(fetchMonth: (year: number, month: number) => Promise<T[]>,
                         request: IReturnCalendarRequest): Promise<DeliveryCalendar> {
    const { monthDate, prevCalendar } = request;
    const cursor = moment(monthDate, 'YYYY-MM-DD', true);
    const requests = [];

    for (let i = 0; i < MONTHS_TO_LOAD; i++) {
      requests.push(fetchMonth(cursor.year(), cursor.month() + 1));
      cursor.add(1, 'month');
    }

    const months = await Promise.all(requests);
    const calendar = prevCalendar || new DeliveryCalendar([]);

    months.forEach((days) => calendar.extend(days || []));

    return calendar;
  }
}
